<template>
  <div class="main-layout">
    <!-- Background Animation (Global) -->
    <div class="page-bg">
      <div class="glow-orb orb-1"></div>
      <div class="glow-orb orb-2"></div>
      <div class="grid-overlay"></div>
    </div>

    <!-- 页头 -->
    <AppHeader 
      :showLogo="computedHeaderConfig.showLogo"
      :title="computedHeaderConfig.title"
      :subtitle="computedHeaderConfig.subtitle"
      :icon="computedHeaderConfig.icon"
      :iconSize="computedHeaderConfig.iconSize"
      :iconColor="computedHeaderConfig.iconColor"
      :compact="computedHeaderConfig.compact"
      :customClass="computedHeaderConfig.customClass"
    >
      <template v-if="$slots['header-extra']" #extra>
        <slot name="header-extra"></slot>
      </template>
    </AppHeader>

    <!-- 主要内容区域 -->
    <main class="main-content">
      <router-view />
    </main>

    <!-- 页脚 -->
    <AppFooter 
      :showMeta="footerConfig.showMeta"
      :customClass="footerConfig.customClass"
      :version="footerConfig.version"
      :buildTime="footerConfig.buildTime"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import { useLayoutProvider, type HeaderConfig } from '@/composables/useLayout'

const route = useRoute()

// 使用布局提供者
const { headerConfig, footerConfig, updateHeaderConfig, resetHeaderConfig } = useLayoutProvider()

// 根据路由自动配置页头
const getRouteHeaderConfig = (): Partial<HeaderConfig> => {
  // 所有页面都显示Logo，除了特殊页面
  const baseConfig = { showLogo: true }
  
  switch (route.name) {
    case 'home':
      return baseConfig
    case 'policies':
      return {
        ...baseConfig,
        title: '政策法规',
        subtitle: '最新政府政策文件解读与分析',
        icon: 'ios-paper'
      }
    case 'company-detail':
      return {
        ...baseConfig,
        title: '公司详情',
        customClass: 'company-detail'
      }
    case 'companies':
      return {
        ...baseConfig,
        title: '公司列表', 
        subtitle: '沪深A股上市公司信息',
        icon: 'ios-briefcase'
      }
    case 'analysis':
      return {
        ...baseConfig,
        title: '分析工具',
        subtitle: '财务分析与数据可视化',
        icon: 'md-trending-up'
      }
    case 'research-reports':
      return {
        ...baseConfig,
        title: '研究报告',
        subtitle: '深度分析 · AI驱动 · 价值洞察',
        icon: 'md-document'
      }
    case 'account-settings':
      return {
        ...baseConfig,
        title: '账户设置',
        subtitle: '管理您的个人信息和偏好',
        icon: 'md-settings'
      }
    default:
      return baseConfig
  }
}

// 路由变化时重置并更新配置
watch(route, () => {
  resetHeaderConfig()
  const routeConfig = getRouteHeaderConfig()
  if (Object.keys(routeConfig).length > 0) {
    updateHeaderConfig(routeConfig)
  }
}, { immediate: true })

// 计算最终的头部配置
const computedHeaderConfig = computed(() => headerConfig)
</script>



<style scoped>
.main-layout {
  height: 100vh; /* Full viewport height */
  display: flex;
  flex-direction: column;
  background-color: var(--app-bg);
  position: relative;
  overflow-x: hidden;
  overflow-y: auto; /* Scroll inside layout */
}

/* Background Effects */
.page-bg {
  position: fixed; /* Fixed relative to viewport so it doesn't scroll away if we scrolled body, but here layout scrolls */
  /* Actually, if layout scrolls, we want bg to stay put? Or scroll? 
     If .main-layout scrolls, absolute to .main-layout will scroll WITH content.
     If we want fixed background, use position: fixed.
  */
  position: fixed; 
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.3;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: #0575e6;
  top: -200px;
  left: -100px;
  animation: floatOrb 15s infinite ease-in-out;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: #00f260;
  bottom: -100px;
  right: -100px;
  animation: floatOrb 18s infinite ease-in-out reverse;
}

.grid-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(circle at center, black 60%, transparent 90%);
}

@keyframes floatOrb {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(30px, 50px); }
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  z-index: 1; /* Above background */
  position: relative;
  /* No overflow here, let MainLayout scroll */
}

/* Ensure footer doesn't shrink */
:deep(.app-footer) {
  flex-shrink: 0;
}
</style>